import {
    trackEvent,
    trackPageview,
    getUserEventItem,
    eventNameConstants,
    trackUserEventItemPageView,
    getSessionPageCount,
} from 'dibs-tracking';
import serverVars from 'server-vars';
import {
    SELLER_TIER_DIAMOND,
    SELLER_TIER_PLATINUM,
    SELLER_TIER_GOLD,
    SELLER_TIER_VETTED,
} from '../../messages/pdpDistinctionsMessages.jsx';
import { trackEcommerce } from '../../tracking/pdpTracking.js';
import { type FC, useEffect, useState, useMemo } from 'react';
import { getPersonalizationTrackingData } from '../../tracking/personalizationTracking.ts';
import {
    useFragment,
    graphql,
    useRelayEnvironment,
    type Environment,
    readInlineData,
} from 'react-relay';
import { useBuyerUrgency } from 'dibs-buyer-urgency/exports/useBuyerUrgency.ts';
import { getEstimationTrackingValue } from 'dibs-pricing-guidance/exports/pricingGuidanceTracking.ts';
import { trackSeoTests } from '../../helpers/trackSeoTests.ts';

// types
import { type PdpSharedPageTracker_viewer$key as Viewer } from './__generated__/PdpSharedPageTracker_viewer.graphql.ts';
import { type PdpSharedPageTracker_item$key as ItemSkus } from './__generated__/PdpSharedPageTracker_item.graphql.ts';

type PageInfo = {
    opt_pageURL: string;
    additional: Record<string, unknown>;
};

const trackUserPageView = ({
    pageInfo,
    environment,
}: {
    pageInfo: PageInfo;
    environment: Environment;
}): void => {
    const userEventItem = getUserEventItem({
        id: pageInfo?.additional?.itemId,
        amount: pageInfo?.additional?.price,
        convertedAmounts: pageInfo?.additional?.convertedAmounts,
    });
    if (userEventItem) {
        trackUserEventItemPageView(environment, [userEventItem]);
    }
};

const skusItemFragment = graphql`
    fragment PdpSharedPageTracker_item on Item @inline {
        isMultiSku
        skus {
            skuId
            images {
                __typename
            }
        }
    }
`;

const getSkuParams = (
    selectedSkuId: string | null,
    itemSkusRef: ItemSkus | null
): { skuType: string; skuId: string | null; numberOfSkuImages: number | null } => {
    const item = readInlineData(skusItemFragment, itemSkusRef);
    const skus = item?.skus;
    const sku = (skus || []).find(_sku => _sku?.skuId === selectedSkuId);
    return {
        skuId: sku?.skuId || null,
        skuType: item?.isMultiSku ? 'multi' : 'single',
        numberOfSkuImages: sku?.images?.length || null,
    };
};

const viewerFragment = graphql`
    fragment PdpSharedPageTracker_viewer on Viewer {
        itemRead(itemIds: $itemIds) {
            ... on Item {
                serviceId
                defaultSkuId
                pdpTrackingVpvParams(
                    userId: $userId
                    userCountryCode: $userCountryCode
                    userZipCode: $userZipCode
                )
                activePromotion(userId: $userId) {
                    code
                }
                seller {
                    sellerTiers {
                        isGoldTier
                        isPlatinumTier
                    }
                    dealerSince
                    sellerOrderQuantity
                    isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                }
                viewInRoomPhotos {
                    __typename
                }

                ...PdpSharedPageTracker_item
                ...pricingGuidanceTracking_item
                seoAbTestV2List @include(if: $afterDisplay) {
                    ...trackSeoTests_abTestConfigV2UserVariant
                }
            }
        }
        personalizedItems(userId: $personalizationId, itemId: $itemId, pageSize: 10) {
            associationType
            hasPersonalizedItem
        }
        user(userId: $userId) @include(if: $fetchUser) {
            __typename
        }
        ...useBuyerUrgency_viewer @arguments(itemId: $itemId, userId: $userId)
    }
`;

export const PdpSharedPageTracker: FC<{
    viewer: Viewer;
    selectedSkuId: string | null;
}> = ({ viewer: viewerRef, selectedSkuId }) => {
    const environment = useRelayEnvironment();
    const viewer = useFragment(viewerFragment, viewerRef);
    const { showBuyerUrgencyBanner } = useBuyerUrgency({ viewer });
    const [shouldFireTracking, setShouldFireTracking] = useState<boolean>(false);

    const { personalizedItems, itemRead } = viewer || {};
    const item = itemRead?.[0];
    const { pdpTrackingVpvParams, activePromotion, seller, viewInRoomPhotos, defaultSkuId } =
        item || {};
    const firstDibsEstimate = getEstimationTrackingValue({ item: item || null });
    const hasViewInRoom = (viewInRoomPhotos || []).length;

    const buyerDisplayablePromo = activePromotion?.code;

    const {
        isRewardsTier2,
        sellerTiers,
        dealerSince: dealerYearJoined,
        sellerOrderQuantity: dealerSales,
    } = seller || {};
    const { isGoldTier, isPlatinumTier } = sellerTiers || {};

    let dealerStanding = SELLER_TIER_VETTED;
    if (isGoldTier) {
        dealerStanding = SELLER_TIER_GOLD;
    } else if (isPlatinumTier) {
        dealerStanding = SELLER_TIER_PLATINUM;
    } else if (isRewardsTier2) {
        dealerStanding = SELLER_TIER_DIAMOND;
    }

    const additionalTrackingData = useMemo(() => {
        return getPersonalizationTrackingData({ personalizedItems });
    }, [personalizedItems]);

    useEffect(() => {
        setShouldFireTracking(true);
    }, [selectedSkuId]);

    useEffect(() => {
        const seoAbTests = serverVars.get('seoAbTests');
        if (seoAbTests) {
            for (const testName in seoAbTests) {
                if (Object.prototype.hasOwnProperty.call(seoAbTests, testName)) {
                    const variant = seoAbTests[testName];
                    // Hacking in A/B test tracking event for SEO test POC
                    // TODO: Remove once POC ends and SEO test type is added to framework
                    trackEvent({
                        category: 'a/b testing v3',
                        eventName: eventNameConstants.EVENT_AB_TESTING,
                        action: testName,
                        interaction_type: testName,
                        label: variant,
                        step_interaction_name: variant,
                        value: undefined,
                    });
                }
            }
        }
    }, []);

    const pageInfo = useMemo(() => {
        const { url: opt_pageURL, ...additionalPdpTrackingParams } = pdpTrackingVpvParams || {};
        return {
            opt_pageURL,
            additional: {
                dealerSales,
                hasViewInRoom,
                dealerStanding,
                soldPriceRange: firstDibsEstimate,
                firstDibsEstimate,
                dealerYearJoined,
                buyerDisplayablePromo,
                buyerUrgency: showBuyerUrgencyBanner,
                pageCountViewed: getSessionPageCount(),
                ...additionalTrackingData,
                ...(additionalPdpTrackingParams || {}),
                ...getSkuParams(selectedSkuId || defaultSkuId || null, item || null),
            },
        };
    }, [
        item,
        dealerSales,
        defaultSkuId,
        hasViewInRoom,
        dealerStanding,
        firstDibsEstimate,
        selectedSkuId,
        dealerYearJoined,
        pdpTrackingVpvParams,
        buyerDisplayablePromo,
        additionalTrackingData,
        showBuyerUrgencyBanner,
    ]);

    useEffect(() => {
        if (shouldFireTracking) {
            setShouldFireTracking(false);
            trackPageview(pageInfo);
            trackUserPageView({ pageInfo, environment });
            trackEcommerce(item, selectedSkuId);
            trackSeoTests({ seoAbTestV2List: item?.seoAbTestV2List });
        }
    }, [shouldFireTracking, environment, item, pageInfo, selectedSkuId]);

    return null;
};
